import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { Package, Plus, Pencil, Archive, RotateCcw } from "lucide-react";
import { api, apiError } from "@/lib/api";
import { PageHeader, Loading, ErrorState, EmptyState } from "@/components/Bits";
import { Modal, Field, inputCls, primaryBtnCls, ghostBtnCls, Table, Td } from "@/components/Kit";
import { NoAccess } from "@/components/AppShell";
import { useAuth } from "@/context/AuthContext";
import { inr } from "@/lib/format";

const emptyForm = () => ({
  package_id: "",
  code: "",
  name: "",
  category: "General",
  description: "",
  price: "",
  inclusions: "",
  included_notes: "",
  extras: "",
  active: true,
});

export function parseExtraLines(raw) {
  const lines = String(raw || "")
    .split(/\n|;/)
    .map((row) => row.trim())
    .filter(Boolean);

  return lines.map((line, index) => {
    const separator = line.indexOf("::");
    if (separator < 1) throw new Error(`Extra ${index + 1} must use Name:: Description | Charge.`);
    const name = line.slice(0, separator).trim();
    const details = line.slice(separator + 2).trim();
    const chargeSeparator = details.lastIndexOf("|");
    if (chargeSeparator < 0) throw new Error(`Extra ${index + 1} needs a charge after '|'.`);
    const description = details.slice(0, chargeSeparator).trim();
    const rawCharge = details.slice(chargeSeparator + 1).trim().replace(/^[^0-9+-]*/, "");
    const charge = Number(rawCharge);
    if (name.length < 2) throw new Error(`Extra ${index + 1} needs a name of at least 2 characters.`);
    if (!rawCharge || !Number.isFinite(charge) || charge <= 0) {
      throw new Error(`Extra ${index + 1} charge must be a valid number greater than zero.`);
    }
    return {
      name,
      description,
      charge,
      tax_percent: 0,
      quantity_default: 1,
      active: true,
    };
  }).filter((item) => item.name);
}

export default function HospitalPackages() {
  const { can } = useAuth();
  const canManage = can("hospital.packages.manage");
  const [items, setItems] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(emptyForm());
  const [editing, setEditing] = useState(null);
  const [saving, setSaving] = useState(false);
  const [updatingId, setUpdatingId] = useState("");

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/hospital/packages");
      setItems(data.items || []);
      setError("");
      return true;
    } catch (e) {
      setError(apiError(e));
      return false;
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (!canManage) return;
    load();
  }, [canManage, load]);

  const openNew = () => {
    setEditing(null);
    setForm(emptyForm());
    setOpen(true);
  };

  const openEdit = (item) => {
    setEditing(item);
    setForm({
      package_id: item.package_id || "",
      code: item.code || "",
      name: item.name || "",
      category: item.category || "General",
      description: item.description || "",
      price: String(item.price ?? ""),
      inclusions: (item.inclusions || []).join("\n"),
      included_notes: item.included_notes || "",
      extras: (item.extras || []).map((x) => `${x.name}:: ${x.description || ""}${x.charge ? ` | ${inr(x.charge)}` : ""}`).join("\n"),
      active: item.active !== false,
    });
    setOpen(true);
  };

  const submit = async (e) => {
    e.preventDefault();
    if (saving) return;
    setError("");
    setSaving(true);
    try {
      const name = form.name.trim();
      const price = Number(form.price);
      if (name.length < 2) throw new Error("Package name must be at least 2 characters.");
      if (!form.price.trim() || !Number.isFinite(price) || price <= 0) {
        throw new Error("Package price must be a valid number greater than zero.");
      }
      const extras = parseExtraLines(form.extras);
      const payload = {
        code: form.code.trim(),
        name,
        category: form.category.trim() || "General",
        description: form.description.trim(),
        price,
        inclusions: form.inclusions.split(/\n|,/).map((s) => s.trim()).filter(Boolean),
        included_notes: form.included_notes.trim(),
        extras,
        active: form.active,
        doctor_fee_support: false,
      };

      if (editing) {
        await api.patch(`/hospital/packages/${editing.package_id}`, payload);
        toast.success("Package updated.");
      } else {
        await api.post("/hospital/packages", payload);
        toast.success("Package created.");
      }
      if (await load()) setOpen(false);
    } catch (err) {
      const message = apiError(err);
      setError(message);
      toast.error(message);
    } finally {
      setSaving(false);
    }
  };

  const toggleActive = async (item) => {
    if (updatingId) return;
    setUpdatingId(item.package_id);
    setError("");
    try {
      const active = item.active === false;
      await api.patch(`/hospital/packages/${item.package_id}`, { active });
      if (await load()) toast.success(active ? "Package restored." : "Package archived.");
    } catch (err) {
      const message = apiError(err);
      setError(message);
      toast.error(message);
    } finally {
      setUpdatingId("");
    }
  };

  if (!canManage) return <NoAccess what="Special packages" />;

  return (
    <div data-testid="hospital-packages-page">
      <PageHeader
        overline="Hospital"
        title="Special Packages"
        subtitle="Manage package catalogues, bundled pricing, inclusions, and package extras for treatment workflows."
        actions={
          <button onClick={openNew} className={primaryBtnCls} data-testid="add-package-btn">
            <Plus className="h-4 w-4" /> Add package
          </button>
        }
      />

      {error && <div className="space-y-3"><ErrorState message={error} /><button type="button" onClick={load} className={ghostBtnCls} data-testid="retry-packages-btn">Retry loading packages</button></div>}
      {!items && loading ? <Loading label="Loading packages…" /> : null}

      {items && items.length === 0 && !error ? (
        <EmptyState title="No packages yet" subtitle="Create the first special package for your hospital services." testid="hospital-package-empty" />
      ) : null}

      {items && items.length > 0 && (
        <Table
          testid="hospital-packages-table"
          columns={["Package", "Category", "Price", "Status", "Actions"]}
          rows={items}
          renderRow={(item) => (
            <tr key={item.package_id} data-testid={`hospital-package-row-${item.package_id}`}>
              <Td className="font-medium text-slate-900">{item.name}</Td>
              <Td className="text-slate-600">{item.category || "General"}</Td>
              <Td className="text-slate-700">{inr(item.price || 0)}</Td>
              <Td>
                <span className={`text-xs font-medium ${item.active === false ? "text-slate-500" : "text-emerald-600"}`}>
                  {item.active === false ? "Archived" : "Active"}
                </span>
              </Td>
              <Td>
                <div className="flex gap-2">
                  <button onClick={() => openEdit(item)} className={ghostBtnCls} data-testid={`edit-package-${item.package_id}`}>
                    <Pencil className="h-3.5 w-3.5" />
                  </button>
                  <button onClick={() => toggleActive(item)} className={ghostBtnCls} data-testid={`toggle-package-${item.package_id}`}>
                    {updatingId === item.package_id ? <RotateCcw className="h-3.5 w-3.5 animate-spin" /> : item.active === false ? <RotateCcw className="h-3.5 w-3.5" /> : <Archive className="h-3.5 w-3.5" />}
                  </button>
                </div>
              </Td>
            </tr>
          )}
        />
      )}

      <Modal open={open} onClose={() => setOpen(false)} title={editing ? "Edit package" : "Add package"} wide testid="hospital-package-modal">
        <form onSubmit={submit} className="space-y-4">
          <div className="grid gap-4 md:grid-cols-2">
            <Field label="Code"><input className={inputCls} value={form.code} onChange={(e) => setForm({ ...form, code: e.target.value })} /></Field>
            <Field label="Name *"><input className={inputCls} value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} /></Field>
            <Field label="Category *"><input className={inputCls} value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })} /></Field>
            <Field label="Price *"><input type="number" min="0" step="0.01" className={inputCls} value={form.price} onChange={(e) => setForm({ ...form, price: e.target.value })} /></Field>
          </div>

          <Field label="Description"><textarea className={inputCls} rows={3} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} /></Field>
          <Field label="Inclusions (one per line)"><textarea className={inputCls} rows={4} value={form.inclusions} onChange={(e) => setForm({ ...form, inclusions: e.target.value })} /></Field>
          <Field label="Included notes"><textarea className={inputCls} rows={2} value={form.included_notes} onChange={(e) => setForm({ ...form, included_notes: e.target.value })} /></Field>
          <Field label="Extras (one per line; format: Name:: Description | Charge)"><textarea className={inputCls} rows={4} value={form.extras} onChange={(e) => setForm({ ...form, extras: e.target.value })} /></Field>

          <label className="flex items-center gap-2 text-sm text-slate-700">
            <input type="checkbox" checked={form.active} onChange={(e) => setForm({ ...form, active: e.target.checked })} />
            Active package
          </label>

          <div className="flex justify-end gap-3">
            <button type="button" onClick={() => setOpen(false)} className={ghostBtnCls}>Cancel</button>
            <button type="submit" disabled={saving} className={primaryBtnCls}>{saving ? "Saving…" : editing ? "Save changes" : "Create package"}</button>
          </div>
        </form>
      </Modal>
    </div>
  );
}
